/**
 * This file contains some functions shared between the main and 11ty builds.
 * Since the 11ty build needs to be very slim in JS, this file should not
 * require other files, especially anything third-party like moment.
 */

//
// Below originally from src/office/CarouselView.jsx
//

const CAROUSEL_CONSTANTS = {
	MAX_WIDTH: 1200, //This is the max-width for all section content across sell pages.
	ARROW_SIZE: 48, //This should match arrow size in css (4rem)
	PAGE_MARGIN: 24, //In px
	WINDOW_MARGIN: 12, //In px
	MIN_PAGE_WIDTH: 188, // Already includes 2 * page_margin
	MAX_PAGE_WIDTH: 220,
	SCROLL_BAR_WIDTH: 15, //Only used to throw in a little extra margin
	PAGES_VISIBLE_CUTOFFS: [
		[1150, 5],
		[900, 4],
		[700, 3],
		[500, 2],
	],
};

//
// Below originally from shared/BeastBuilderShared.js.
//

/**
 * Get the URL that displays a user avatar given their avatarData.
 *
 * This is mostly duplicated in `views/accounts.hbs`, so make sure to copy changes over there as well!
 *
 * @param {object} avatarData The object in the user model avatarData field.
 *     Currently supports {color, num} for pre Beast Builder default avatars
 *     or {hash, userID} for Beast Builder avatars.
 * @return {string}
 */
const getAvatarURL = function(avatarData) {
	if (!avatarData) return null;

	const {num, scheme, hash, userID = 1} = avatarData;
	const baseURL = global.BASE_URL || "/";
	const cdnDomain = global.AVATAR_CDN_DOMAIN_NAME;

	if (num) {
		return baseURL + `assets/images/icons/default-avatars/${num}.svg`;
	} else if (hash) {
		const filename = `${userID}-${hash}.jpeg`;
		return cdnDomain
			? `https://${cdnDomain}/${filename}`
			: baseURL + `public/beast/${filename}`;
	} else {
		const filename = `${getDefaultAvatarNameForUser(userID, scheme)}.jpeg`;
		return cdnDomain
			? `https://${cdnDomain}/${filename}`
			: baseURL + `public/beast/${filename}`;
	}
};

const AVATAR_SCHEME = 3;

// This is duplicated in `views/accounts.hbs`, so make sure to copy changes over there as well!
const defaultBeastColorOptions = [1, 3, 6, 8, 9, 10, 11];

// This is duplicated in `views/accounts.hbs`, so make sure to copy changes over there as well!
const getRandomBeastColorForUser = (userID, scheme) => {
	if (userID === 1) {
		return 10;
	}
	return defaultBeastColorOptions[userID % defaultBeastColorOptions.length];
};

const getDefaultAvatarFilenames = () => {
	return [
		"1-default.jpeg",
		...defaultBeastColorOptions.map(c => `default-simpleblob${c}.jpeg`),
	];
};

// This is duplicated in `views/accounts.hbs`, so make sure to copy changes over there as well!
const getDefaultAvatarNameForUser = (userID, scheme) => {
	if (userID === 1) {
		return "1-default";
	}
	return `default-simpleblob${getRandomBeastColorForUser(userID, scheme)}`;
};

//
// Below originally from core/util/CopyTable.js.
//

const commonErrorTypes = {
	E_DATABASE:
		"Uh oh! Our server is experiencing difficulties. Please wait awhile, then try again.",
};

// errors that can occur due to domain issue
const copyForCommonDomainErrors = function(messageType, info) {
	const {username = "", domain = ""} = info || {};
	let message = "";
	if (messageType === "E_INVALID_DOMAIN") {
		message = `The domain ${domain + " "}does not exist.`;
	} else if (messageType === "E_NO_USER_FOR_DOMAIN") {
		message = `Username ${username + " "}does not exist for ${domain}.`;
	}
	return message;
};

// TODO PLATFORM (V2): Delete this? We need to untangle some things
// from React side.
const copyForLoginError = function(code, info) {
	if (code === "E_MISSING_USERNAME") {
		return `Please enter your username.`;
	} else if (code === "E_NON_ASCII_USERNAME") {
		return `Please enter a username that does not contain special characters.`;
	} else if (code === "E_MISSING_PASSWORD") {
		return `Please enter your password.`;
	} else if (code === "E_MISSING_USERNAME|E_MISSING_PASSWORD") {
		return `Please enter your username and your password.`;
	} else if (code === "E_MISSING_USERNAMENUM") {
		return `Don't forget the number at the end of your username!`;
	} else if (code === "E_NO_SUCH_USERNAME") {
		return `The username you entered does not exist.`;
	} else if (code === "E_CODE_NOT_ACTIVATED") {
		// for all intents and purposes, this account doesn't "exist" yet,
		// so the message is the same as E_NO_SUCH_USERNAME for now
		return `The username you entered does not exist.`;
	} else if (code === "E_EMAIL_NOT_CONFIRMED") {
		return `This email has not been confirmed.`;
	} else if (code === "E_RATE_LIMIT") {
		return `A confirmation email has been recently requested. Please check your email.`;
	} else if (code === "E_NO_PASSWORD_SET") {
		// Maybe recommend the user email us? You’re stuck if you get this one.
		return `The username you entered does not have a password.`;
	} else if (code === "E_AOPS_INTEROP_FAILURE") {
		// TODO: figure out something better to say here
		return `There was an issue signing you in. Please try again.`;
	} else if (code === "E_WRONG_PASSWORD") {
		return `The password you entered is incorrect.`;
	} else if (code === "E_PASSWORD_LIMIT") {
		return `You have made too many incorrect attempts to sign in. Please reset your password.`;
	} else if (code === "E_DISCONNECTED") {
		return `No internet connection found. Please reconnect and try again.`;
	} else if (code === "E_NO_COOKIES") {
		//Only for the office login modal, trainer pages show a modal if there are no cookies
		return `Please enable cookies and refresh to sign into BA Online.`;
	} else if (code === "E_AUTHENTICATION_FAIL") {
		//Only for the office login modal, which also shows errors for the steps between login & user load
		return `There was an issue logging you in. Please try again.`;
	} else if (code === "E_USER_LOAD_ERROR") {
		//Only for the office login modal, which also shows errors for the steps between login & user load
		return `There was an issue signing you in. Please try again.`;
	} else if (commonErrorTypes[code]) {
		return commonErrorTypes[code];
	} else if (copyForCommonDomainErrors(code, info)) {
		return copyForCommonDomainErrors(code, info);
	} else if (code) {
		return `We could not sign you in. Wait a bit, then try again.`;
	}
	return null;
};

const passwordRuleCopy = "* Minimum 8 characters.";

// TODO: It seemed messy to try to combine this with CopyTable
// duplicating instead.
const copyForEmailSignupErrors = function(type) {
	const messages = {
		roleNotChosen: "* You must choose a role.",
		agreeNotChecked: "* You must agree to the terms.",
		signUpFail: "There was an issue signing you up.",
		ageNotConfirmed: "You must confirm your age.",
		E_DUPLICATE_EMAIL: "You are already signed up for our mailing list.",
	};
	return messages[type] || messages.signUpFail;
};

const copyForStudentNameErrors = function(type) {
	const messages = {
		E_NO_NAME: "Please enter a name",
		E_DISALLOWED_CHARACTERS: "Student nicknames may only include letters",
		E_DUPLICATE_NAME: "Student nicknames must be unique",
		E_NO_GRADE: "This field is required",
	};
	return messages[type] || "Please enter a valid nickname";
};

const copyForContactUs = function() {
	return {
		errors: {
			requiredFieldEmpty: "Required fields are empty.",
			messageTooShort: "The message must be at least 15 characters long.",
			noReasonSelected: "You must choose a reason for contact.",
			noRoleSelected: "You must choose the role that best describes you.",
			invalidEmail: "Please enter a valid email.",
			emailMismatch: "The emails you entered do not match.",
		},
		reasons: {
			recommendations: "Recommendations",
			orderingInfo: "Orders and Sales",
			schools: "For Schools",
			prospectiveCustomerOther: "Other",
			account: "Account Help",
			ordersAndBilling: "Orders & Billing (Existing)",
			techSupport: "Technical Support",
			customerOther: "Other",
		},
	};
};

const getAgeRangeByGrade = function(baLevel, isPuzzleBook, isScienceBook) {
	baLevel = baLevel.toString().charAt(0); // in case '3S' comes in.
	// shouldn't this information be stored in the YAML, or at the very least,
	// the database?
	const ageRanges = {
		math: {
			1: [6, 8],
			2: [7, 9],
			3: [8, 10],
			4: [9, 12],
			5: [10, 13],
		},
		puzzle: {
			1: [7, "\u221E"],
			2: [7, "\u221E"],
			3: [9, "\u221E"],
			4: [10, "\u221E"],
			5: [10, "\u221E"],
		},
		science: {
			1: [6, 8],
			2: [7, 9],
			3: [8, 10],
			4: [9, 12],
			5: [10, 13],
		},
	};
	const subject = (() => {
		if (isScienceBook) {
			return "science";
		}
		return isPuzzleBook ? "puzzle" : "math";
	})();

	return ageRanges[subject][baLevel].map(el => el.toString()).join("\u2011");
};

// Gets the value in a url query string
const getQueryValue = (qs, field) => {
	const params = new URLSearchParams(qs);
	return params.get(field);
};

// Retrieves value of session storage item with manually added expire value. If
// value is expired, it will pretend the key doesn't exist.
function getSessionStorageWithExpiry(key) {
	const item = global.window.sessionStorage.getItem(key);
	// If item is expired, return null
	if (item && item !== 1 && item < Date.now()) {
		return null;
	}
	return item;
}

module.exports = {
	CAROUSEL_CONSTANTS,
	AVATAR_SCHEME,
	getAvatarURL,
	getRandomBeastColorForUser,
	getDefaultAvatarNameForUser,
	getDefaultAvatarFilenames,
	commonErrorTypes,
	passwordRuleCopy,
	copyForCommonDomainErrors,
	copyForLoginError,
	copyForEmailSignupErrors,
	copyForStudentNameErrors,
	copyForContactUs,
	getAgeRangeByGrade,
	getQueryValue,
	getSessionStorageWithExpiry,
};
